import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { fmtDateTime } from '../lib/format';
import { useNotifications, useUnreadCount, useMarkAllRead, type AppNotification } from '../features/notifications/api';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const { data: unread = 0 } = useUnreadCount();
  const { data: items } = useNotifications(open);
  const markRead = useMarkAllRead();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  // Otevření = označit vše přečtené (vyčistí badge).
  useEffect(() => {
    if (open && unread > 0) markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Klik mimo zavře.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const go = (n: AppNotification) => {
    setOpen(false);
    if (n.link) (navigate as (o: { to: string }) => void)({ to: n.link });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative h-9 w-9 rounded-md border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-sm"
        title="Notifikace"
        aria-label="Notifikace"
      >
        🔔
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-30 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-xl">
          <div className="border-b border-zinc-200 dark:border-zinc-800 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Notifikace
          </div>
          <div className="max-h-96 overflow-auto">
            {!items || items.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-zinc-400">Žádné notifikace.</p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => go(n)}
                  className={`block w-full px-3 py-2.5 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 ${n.readAt ? '' : 'bg-sky-50/50 dark:bg-sky-950/20'}`}
                >
                  <div className="text-zinc-700 dark:text-zinc-200">{n.body}</div>
                  <div className="mt-0.5 text-[11px] text-zinc-400">{fmtDateTime(n.createdAt)}</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
